<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import striptags from 'striptags'
import { type PromptAd, asPromptAd } from '@/types/ad'
import { getAd, getSponsorLink } from '@/api/ads'

const { colors } = useTheme()
const followedTopicsLoaded = useFollowedTopicsLoaded()
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

const target = ref<HTMLElement>()
const isVisible = ref(false)
const variant = ref('default')
const banner = ref<PromptAd | null>()
const hasError = ref(false)
const tryingToLoad = ref(true)

const checkElementVisibility = () => {
  if (!target.value)
    return false

  if (banner.value)
    return true

  const rect = target.value.getBoundingClientRect()
  return (
    rect.top >= 0
      && rect.left >= 0
      && rect.bottom <= ((window.innerHeight || document.documentElement.clientHeight) + 32)
      && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

const startInterval = () => {
  const interval = setInterval(() => {
    const isTargetVisible = checkElementVisibility()
    if (isTargetVisible) {
      isVisible.value = checkElementVisibility()
      clearInterval(interval)
    }
  }, 50)
}

onMounted(async () => {
  setTimeout(() => {
    // if it didn't start in 3000ms, force start (user is not logged in)
    if (!followedTopicsLoaded.value)
      followedTopicsLoaded.value = true
  }, 3000)

  if (followedTopicsLoaded.value)
    startInterval()
})

whenever(followedTopicsLoaded, () => {
  setTimeout(startInterval, 100)
})

const loadAd = async () => {
  hasError.value = false
  try {
    banner.value = asPromptAd(await getAd({ type: 'in-content', not_id: undefined })) ?? null
  }
  catch (_) {
    hasError.value = true
    banner.value = null
  }
}

whenever(isVisible, loadAd)

const avatarSize = computed(() => {
  return isMobile.value ? 40 : 60
})

const optimizedPromptAvatar = computed(() => {
  if (!banner.value?.user?.avatar)
    return '/prompt_avatar.png'

  return optimizeImage(banner.value?.user.avatar, {
    width: avatarSize.value * 2, height: avatarSize.value * 2,
  })
})

const stripHTMLDescription = (html: string) => {
  return striptags(html, ['strong', 'em'])
}
</script>

<script lang="ts">
export default defineComponent({
  name: 'AppPubPrompt',
})
</script>

<template>
  <div ref="target">
    <template v-if="!hasError">
      <div
        v-if="banner"
      >
        <NuxtLink :to="getSponsorLink(banner.id)" target="_blank">
          <div
            bg-transparent cursor-pointer
            flex relative justify-center break-all hover:z-2
            border-1 border-transparent border-b-gray-lighter hover:rounded-2 hover:border-gray-lighter
            :pb="variant === 'default' ? 8 : 6"
            :px="isMobile || variant === 'default' ? '10px' : 3"
            class="pt-8"
            :class="[
              $style.pCard,
              variant === 'default' && 'pt-8',
              variant !== 'default' && 'pt-6',
            ]"
          >
            <div flex="~ col" flex-grow gap-2>
              <div flex gap-2>
                <div flex flex-grow gap="10px">
                  <img
                    v-if="variant === 'default' && optimizedPromptAvatar" :src="optimizedPromptAvatar"
                    :width="avatarSize" :height="avatarSize"
                    h-max rounded-2 b-1 border-soft-gray
                    :title="`${banner.title} prompt avatar`"
                    :alt="`${banner.title} prompt avatar`"
                  >

                  <div>
                    <div
                      flex flex-col flex-1
                      :gap="variant === 'default' && !isMobile ? '6px' : '2px'"
                    >
                      <Heading
                        v-if="variant === 'default'" level="5-light" :max-lines="2"
                        :color="colors.softBlack" :title="banner.title"
                        :heading-styles="{ fontSize: '15px' }"
                      >
                        {{ banner.title }}
                      </Heading>

                      <Text v-else variant="small" bold :max-lines="1" :color="colors.softBlack">
                        {{ banner.title }}
                      </Text>
                    </div>

                    <Text
                      v-if="variant === 'default' && !isMobile"
                      :max-lines="1" :color="colors.bluishDarker"
                      variant="small" mt="2px"
                      break-words whitespace-preline
                    >
                      <div v-html="stripHTMLDescription(banner.description)" />
                    </Text>

                    <section flex="~ wrap" flex-grow items-center mt-2 :gap="(isMobile || variant === 'small') ? 5 : 4" :justify="variant === 'default' ? 'start' : 'between'">
                      <div flex gap-1 items-center>
                        <Text variant="caption" bold :color="colors.bluishSofter">
                          Ad
                        </Text>
                        <UserChip v-if="banner.user" :user="banner.user" :should-redirect="false" variant="text" />
                      </div>

                      <Text variant="small" :color="colors.bluishSofter">
                        <div class="flex items-center font-bold color-dark-gray gap-1">
                          {{ banner.cta || 'Open' }}
                          <span i-ph-arrow-up-right-bold />
                        </div>
                      </Text>
                    </section>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </nuxtlink>
      </div>
      <div v-else-if="tryingToLoad">
        <div
          bg-transparent cursor-pointer
          flex relative justify-center break-all hover:z-2
          border-1 border-transparent border-b-gray-lighter hover:rounded-2 hover:border-gray-lighter
          :pb="variant === 'default' ? 8 : 6"
          :px="isMobile || variant === 'default' ? '10px' : 3"
          class="pt-8"
          :class="[
            $style.pCard,
            variant === 'default' && 'pt-8',
            variant !== 'default' && 'pt-6',
          ]"
        >
          <div flex="~ col" flex-grow gap-2>
            <div flex gap-2>
              <div flex flex-grow gap="10px">
                <AppPlaceholder :width="`${avatarSize}px`" :height="`${avatarSize}px`" />

                <div flex-grow>
                  <div
                    flex flex-col flex-1
                    :gap="variant === 'default' && !isMobile ? '6px' : '2px'"
                  >
                    <AppPlaceholder width="40%" height="18px" />
                  </div>

                  <Text
                    v-if="variant === 'default' && !isMobile"
                    :max-lines="1" :color="colors.bluishDarker"
                    mt="2px"
                    break-words whitespace-preline
                  >
                    <AppPlaceholder width="75%" height="16px" />
                  </Text>

                  <section flex="~ wrap" flex-grow items-center mt-2 :gap="(isMobile || variant === 'small') ? 5 : 4" :justify="variant === 'default' ? 'start' : 'between'">
                    <div flex gap-1>
                      <AppPlaceholder width="30px" height="14px" />
                      <AppPlaceholder width="60px" height="14px" />
                    </div>

                    <Text variant="small" :color="colors.bluishSofter">
                      <div class="flex items-center gap-1">
                        <AppPlaceholder width="80px" height="14px" />
                      </div>
                    </Text>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <slot v-else name="fallback" />
  </div>
</template>

<style module lang="scss">
.pCard::before {
  position: absolute;
  content: '';
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-image: linear-gradient(226deg, #F6F6FF 0%, rgba(246, 246, 255, 0.00) 100%);
  z-index: -1;
  transition: opacity 0.35s linear;
  opacity: 0;
}

.pCard:hover {
  &::before {
    opacity: 1;
  }
}

.footerItem {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
}

.saved {
  @apply bg-primary text-white;
}

.notSaved {
  @apply bg-light-bluish text-bluish-softer;
  @apply hover: (bg-primary-light text-primary);
}

.gridCols1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}
.gridCols2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.gridCols3 {
  grid-template-columns: 2fr 1fr;
}
.gridCols4 {
  grid-template-columns: 3fr 1fr;
}

.actionButtons {
  width: 0;
  opacity: 0;
  overflow: hidden;
  transition: opacity .35s linear;
}
</style>
